import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { v5 as uuidv5 } from 'uuid';

import { useCustomChartsPreview } from '@/hooks/useSwr';
import {
  CustomChartCreatePreview,
  CustomChartDataSchema,
  CustomChartsPreviewRequest,
  RunStatsGroupBy,
} from '@/types/schema';

import { ChartFormInput } from '../CustomChartsCrudPane';
import {
  convertFilterFormat,
  createSeriesFromFilters,
} from '../utils/CustomChartsUtils.utils';
import { FEEDBACK_METRICS } from '../utils/constants';
import { useChartTimeFilter } from './useChartTimeFilter';

// random UUID namespace for generating series IDs
const CUSTOM_NAMESPACE = '6ba7b812-9dad-11d1-80b4-00c04fd430c8';

export const getFakeIdForSeries = (name: string, idx: number) =>
  uuidv5(`${name}-${idx}`, CUSTOM_NAMESPACE);

interface ProcessGroupByPreviewDataParams {
  data: CustomChartDataSchema[] | undefined;
  seriesPayload: CustomChartCreatePreview;
  groupBySelection: RunStatsGroupBy;
}

export const processGroupByPreviewData = ({
  data,
  seriesPayload,
  groupBySelection,
}: ProcessGroupByPreviewDataParams) => {
  if (!data) return null;

  // Get unique groups and create series for each
  const uniqueGroups = [
    ...new Set(data.map((item) => item.group).filter(Boolean)),
  ];

  const transformedSeries = uniqueGroups.map((group) => ({
    ...seriesPayload.series[0],
    id: `${seriesPayload.series[0].id}:${group}`,
    group_by: groupBySelection,
  }));

  // Transform data points to match series IDs
  const transformedData = data.map((point) =>
    point.group
      ? { ...point, series_id: `${point.series_id}:${point.group}` }
      : point
  );

  if (transformedSeries.length === 0) {
    return null;
  }

  return {
    previewData: transformedData,
    seriesPayload: {
      ...seriesPayload,
      series: transformedSeries,
    },
  };
};

interface ProcessFeedbackValuesPreviewDataParams {
  data: CustomChartDataSchema[] | undefined;
  seriesPayload: CustomChartCreatePreview;
  feedbackKey: string;
}

export const processFeedbackValuesPreviewData = ({
  data,
  seriesPayload,
  feedbackKey,
}: ProcessFeedbackValuesPreviewDataParams) => {
  if (!data || data.length === 0) return null;

  // Get all unique categorical values across all data points
  const uniqueCategoricalValues = new Set<string>();

  data.forEach((point) => {
    const feedbackValue = point.value?.[feedbackKey];
    if (feedbackValue?.values) {
      Object.keys(feedbackValue.values).forEach((key) => {
        uniqueCategoricalValues.add(key);
      });
    }
  });

  if (uniqueCategoricalValues.size === 0) return null;

  // Create transformed series for each categorical value
  const transformedSeries = Array.from(uniqueCategoricalValues).map(
    (categoricalValue) => ({
      ...seriesPayload.series[0],
      id: `${seriesPayload.series[0].id}:${categoricalValue}`,
      name: `${categoricalValue}`,
      categorical_value: categoricalValue,
      feedback_key: null, // explicitly set to null to match what gets returned in normal chart data api.
    })
  );

  // Transform data points to create separate data points for each categorical value
  const transformedData: CustomChartDataSchema[] = [];

  data.forEach((point) => {
    const feedbackValue = point.value?.[feedbackKey];
    if (feedbackValue?.values && Object.keys(feedbackValue.values).length > 0) {
      Object.entries(feedbackValue.values).forEach(
        ([categoricalValue, value]) => {
          transformedData.push({
            series_id: `${point.series_id}:${categoricalValue}`,
            timestamp: point.timestamp,
            value: value as number,
            group: point.group,
          });
        }
      );
    } else {
      // If values object is empty or doesn't exist, create a data point with value 0 for each categorical value
      Array.from(uniqueCategoricalValues).forEach((categoricalValue) => {
        transformedData.push({
          series_id: `${point.series_id}:${categoricalValue}`,
          timestamp: point.timestamp,
          value: 0,
          group: point.group,
        });
      });
    }
  });

  return {
    previewData: transformedData,
    seriesPayload: {
      ...seriesPayload,
      series: transformedSeries,
    },
  };
};

export const useCustomChartsPreviewData = (formValues: ChartFormInput) => {
  const { timeModel, setTimeModel, customChartsParams } = useChartTimeFilter({
    max_num_buckets: 60,
    customStride: formValues.customStride,
    timeRange: formValues.timeRange,
  });

  const dataSeriesStringified = JSON.stringify(formValues.dataSeries);
  const comparisonMetricsStringified = JSON.stringify(
    formValues.comparisonMetrics
  );
  const sessionIdsStringified = JSON.stringify(formValues.sessionIds);
  const groupBySelectionStringified = JSON.stringify(
    formValues.groupBySelection
  );

  const seriesPayload = useMemo(() => {
    if (!formValues.sessionIds?.length || !formValues.metric) {
      return {
        series: [],
        common_filters: {
          session: [],
          filter: undefined,
          tree_filter: undefined,
          trace_filter: undefined,
        },
      } as CustomChartCreatePreview;
    }
    return {
      series: createSeriesFromFilters({
        sessionIds: formValues.sessionIds,
        metric: formValues.metric,
        comparisonMetrics: formValues.comparisonMetrics,
        dataSeriesFilters: formValues.dataSeries,
        feedbackKey: FEEDBACK_METRICS.includes(formValues.metric)
          ? formValues.feedbackKey
          : undefined,
        groupBySelection: formValues.groupBySelection,
      }).map((s, idx) => ({
        ...s,
        id: getFakeIdForSeries(s.name, idx),
      })),
      common_filters: {
        session: formValues.sessionIds,
        ...convertFilterFormat(formValues.filters, 'seriesFilters'),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.filters,
    sessionIdsStringified,
    formValues.metric,
    comparisonMetricsStringified,
    dataSeriesStringified,
    formValues.feedbackKey,
    groupBySelectionStringified,
  ]);

  const payload: CustomChartsPreviewRequest = useMemo(() => {
    return {
      chart: seriesPayload,
      bucket_info: customChartsParams,
    };
  }, [seriesPayload, customChartsParams]);

  const [debouncedPayload] = useDebounce(payload, 500);
  const { data, error, isLoading } = useCustomChartsPreview(
    debouncedPayload.chart.common_filters.session?.length
      ? debouncedPayload
      : null
  );

  const previewData = {
    previewData: data?.data ?? [],
    seriesPayload,
    previewDataIsLoading: isLoading,
    previewDataError: error,
    previewTimeModel: timeModel,
    setPreviewTimeModel: setTimeModel,
    previewChartsParams: customChartsParams,
  };

  // With group by we need to transform data and series into
  // the right format to be rendered by the chart component
  if (formValues.groupBySelection && data?.data) {
    const processedData = processGroupByPreviewData({
      data: data.data,
      seriesPayload,
      groupBySelection: formValues.groupBySelection,
    });

    if (processedData) {
      return {
        ...previewData,
        ...processedData,
      };
    }
  }

  if (formValues.metric === 'feedback_values' && data?.data) {
    const processedData = processFeedbackValuesPreviewData({
      data: data.data,
      seriesPayload,
      feedbackKey: formValues.feedbackKey || '',
    });

    if (processedData) {
      return {
        ...previewData,
        ...processedData,
      };
    }
  }

  return previewData;
};
